import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {when} from 'lit/directives/when.js';
import {repeat} from 'lit/directives/repeat.js';
import {property} from 'lit/decorators/property.js';
import {msg} from '@lit/localize';

import {connect} from 'pwa-helpers';
import store from '../../store';
import {selectSpcAttributes, selectSpcConfiguration, selectSpcConfigurations, selectSpcIsSatisfied} from '../../selectors';
import {
	selectGroupedHeads
} from '../olivioSelectors';
import {getValueOfFeature} from '../../util/values';
import {conditional, conditionalWithConditions} from '../../util/conditional';
import download from '../../../icons/download';

@customElement('olivio-downloads-for-sidebar')
export class OlivioDownloadsForSidebar extends connect(store)(LitElement) {

	static styles = [
		css`
			:host {
			}
			.title {
				font: var(--font-widget-label);
				color: var(--color-white);
				padding-bottom: 0.4cqw;
			}
			button {
				text-align: left;
				font: var(--font-widget-label);
				color: var(--color-black);
				display: block;
				flex-direction: column;
				justify-content: space-between;
				gap: var(--padding);
				background-color: transparent;
				border: none;
				cursor: pointer;
			}
			.downloadItems {
				display: flex;
				flex-direction: column;
				flex-grow: 1;
				margin-bottom: auto;
				padding-bottom: var(--padding);
			}
			.downloadItem {
				display: flex;
				flex-direction: row;
				justify-content: space-between;
				text-align: left;
				font: var(--font-widget-label);
				color: var(--color-white);
				padding: var(--very-small-padding) 0;
				margin-bottom: 0;
				width: 100%;
				background-color: transparent;
				border: none;
				cursor: pointer;
				border-bottom: 1px solid var(--color-grey3);
			}

			.downloadItem:first-child {
				border-top: 1px solid var(--color-grey3);
			}


			.downloadItem:disabled .label,
			.downloadItem.disabled .label {
				color: var(--color-grey5);
				border-color: var(--color-grey5);
			}

			.icon {
				display: inline-block;
				height: 16px;
				width: 16px;
				margin-right: 10px;
				color: var(--color-white);
			}

			.downloadButton .icon {
				color: var(--color-black);
			}

			.icon.state {
				color: var(--color-warn);
			}

			.icon.state.satisfied {
				color: var(--color-ok);
			}

			.icon svg {
				width: 100%;
				height: auto;
			}


			.heads {
				font: var(--font-specsheet-print-small-text);
				display: inline-flex;
				flex-direction: row;
				font-size: 0.5cqw;
				gap: 0.2cqw;
				align-items: center;
			}
			.head {
				display: flex;
				justify-content: center;
				align-items: center;
				background-color: var(--color-white);
				color: var(--color-black);;
				height: 0.8cqw;
				width: 0.8cqw;
				border-radius: 1cqw;
			}
		`
	];

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
		this.groupsAvailable = false;
	}

	stateChanged(state) {
		this.state = state;
		this.conditionalVariantValues = state.productData.conditionalVariantValues;

		this.spcConfiguration = selectSpcConfiguration(state);
		this.spcConfigurations = selectSpcConfigurations(state);

		if (this.spcConfiguration) {
			this.spcAttributes = selectSpcAttributes(state);
			this.spcIsSatisfied = selectSpcIsSatisfied(state);

			// group heads on pages based on properties
			this.groupedHeads = selectGroupedHeads(state);
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback();
	}

	connectedCallback() {
		super.connectedCallback();
	}

	getLdtFile(headData) {
		if (this.spcConfigurations[headData.instanceId]) {
			const housingSize = getValueOfFeature('root::HS_HousingSize', this.spcConfigurations[headData.instanceId]);
			const lightDistribution = getValueOfFeature('root::LE_LightDistribution_1', this.spcConfigurations[headData.instanceId]);
			const colourTemperature = getValueOfFeature('root::LE_ColourTemperature_1', this.spcConfigurations[headData.instanceId]);
			const reflectorColour = getValueOfFeature('root::LE_ReflectorColour_1', this.spcConfigurations[headData.instanceId]);
			const dimmingLevel = getValueOfFeature('root::LE_DimmingLevel_1', this.spcConfigurations[headData.instanceId]);
			const accessory = getValueOfFeature('root::LE_Accessories_1', this.spcConfigurations[headData.instanceId]);

			const ldtConditions = [
				'HS_HousingSize.' + housingSize,
				'LE_ColourTemperature_1.' + colourTemperature,
				'LE_LightDistribution_1.' + lightDistribution,
				'LE_ReflectorColour_1.' + reflectorColour,
				'LE_DimmingLevel_1.' + dimmingLevel,
				'LE_Accessories_1.' + accessory
			];
			return conditionalWithConditions(ldtConditions, this.conditionalVariantValues, 'REDTAB.RETURN_LE_LDTFile', 'EditorialTable', 1);
		} else {
			return null;
		}
	}

	download(url) {
		window.open(url);
	}

	render() {
		if (!this.groupedHeads) return null;

		return html`
			<div class="title">LDT:</div>
			<div class="downloadItems">
				${repeat(this.groupedHeads,
					(headData, index) => 'head_' + index,
					(headData) => html`
						${when(headData.head.type === 'luminaire', () => html`
							<button class="downloadItem" @click=${(e) => this.download(this.getLdtFile(headData))}>
								<span class="label">
									${msg('Heads')}
									<span class="heads">
										${repeat(headData.indexes, (i) => i, (i) => html`
											<span class="head">${i + 1}</span>
										`)}
									</span>
								</span> <span class="icon">${download}</span>
							</button>
						`)}
					`)}
			</div>
		`;
	}
}
